import Autocomplete from "@mui/material/Autocomplete"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"

import Column from "../../../common/components/Column"
import { adminApi } from "../axios"
import AdminEntityAutocomplete from "./AdminEntityAutocomplete"

export default function AdminCallingNumberEditDialog({
  open,
  assignment,
  onClose,
  onSave,
  saving,
  errorMessage,
}) {
  const [form, setForm] = useState({
    concierge_user_id: "",
    ownerAccount: null,
    twilio_voice_phone: "",
    twilio_phone_sid: "",
  })
  const [concierge, setConcierge] = useState(null)
  const [accountOptions, setAccountOptions] = useState([])
  const [accountSearchLoading, setAccountSearchLoading] = useState(false)
  const [accountSearchQuery, setAccountSearchQuery] = useState("")

  useEffect(() => {
    if (!assignment) return
    // Typeahead (active concierges via q) instead of a capped preload.
    setConcierge(
      assignment.concierge_user_id
        ? {
            id: assignment.concierge_user_id,
            name: assignment.concierge_name,
            email: assignment.concierge_email,
          }
        : null
    )
    setForm({
      concierge_user_id: assignment.concierge_user_id ?? "",
      ownerAccount: assignment.owner_user_id
        ? {
            id: assignment.owner_user_id,
            email: assignment.owner_email,
            name: assignment.owner_name,
          }
        : null,
      twilio_voice_phone: assignment.twilio_voice_phone || "",
      twilio_phone_sid: assignment.twilio_phone_sid || "",
    })
    setAccountSearchQuery(
      assignment.owner_email
        ? `${assignment.owner_name || assignment.owner_email} (${
            assignment.owner_email
          })`
        : ""
    )
  }, [assignment])

  useEffect(() => {
    if (!open) return undefined
    if (accountSearchQuery.length < 2) {
      setAccountOptions([])
      return undefined
    }
    const timer = setTimeout(() => {
      setAccountSearchLoading(true)
      adminApi
        .searchAccounts(accountSearchQuery)
        .then(({ data }) => setAccountOptions(data))
        .finally(() => setAccountSearchLoading(false))
    }, 300)
    return () => clearTimeout(timer)
  }, [open, accountSearchQuery])

  function handleSubmit(event) {
    event.preventDefault()
    const payload = {
      twilio_voice_phone: form.twilio_voice_phone.trim(),
    }
    const sid = form.twilio_phone_sid.trim()
    if (sid) payload.twilio_phone_sid = sid

    const conciergeChanged =
      String(form.concierge_user_id) !==
      String(assignment?.concierge_user_id ?? "")
    const ownerChanged =
      form.ownerAccount?.id &&
      String(form.ownerAccount.id) !== String(assignment?.owner_user_id ?? "")

    if (conciergeChanged || ownerChanged) {
      payload.concierge_user_id = Number(form.concierge_user_id)
      payload.owner_user_id = Number(form.ownerAccount?.id)
    }

    onSave(payload)
  }

  const canSave =
    form.twilio_voice_phone.trim() &&
    form.concierge_user_id &&
    form.ownerAccount?.id

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Edit / reassign calling number</DialogTitle>
        <DialogContent>
          <Column gap={2} sx={{ pt: 1 }}>
            <AdminEntityAutocomplete
              entity="concierge"
              label="Concierge"
              placeholder="Search active concierges"
              value={concierge}
              onChange={(option) => {
                setConcierge(option)
                setForm((prev) => ({
                  ...prev,
                  concierge_user_id: option ? option.id : "",
                }))
              }}
            />
            <Autocomplete
              options={accountOptions}
              loading={accountSearchLoading}
              value={form.ownerAccount}
              inputValue={accountSearchQuery}
              onChange={(_, value) => {
                setForm((prev) => ({ ...prev, ownerAccount: value }))
                setAccountSearchQuery(
                  value ? `${value.name || value.email} (${value.email})` : ""
                )
              }}
              onInputChange={(_, value) => setAccountSearchQuery(value)}
              getOptionLabel={(option) =>
                option.email
                  ? `${option.name || option.email} (${option.email})`
                  : ""
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Parent account"
                  size="small"
                  helperText="Search by name or email (min. 2 characters)"
                  fullWidth
                  required
                />
              )}
            />
            <TextField
              label="Twilio number (E.164 or formatted)"
              value={form.twilio_voice_phone}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  twilio_voice_phone: e.target.value,
                }))
              }
              size="small"
              fullWidth
              required
              autoFocus
            />
            <TextField
              label="Twilio phone SID (optional)"
              value={form.twilio_phone_sid}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  twilio_phone_sid: e.target.value,
                }))
              }
              size="small"
              fullWidth
              helperText="Leave blank to keep the stored SID unchanged on save."
            />
            <Typography variant="body2" color="text.secondary">
              Reassigning moves this DID (and greeting) to another active
              concierge assignment. The target pair must not already have a
              different number.
            </Typography>
            {errorMessage ? (
              <Typography color="error" variant="body2">
                {errorMessage}
              </Typography>
            ) : null}
          </Column>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || !canSave}
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

AdminCallingNumberEditDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  assignment: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  errorMessage: PropTypes.string,
}
